// Screen 3 — Home ("your people"). Connected people + an Invite action that
// mints an open link and shares it (native share sheet, clipboard fallback).
// Empty state: the invite action IS the screen.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { listConnections, listPendingOpenLinks } from '../services/network';
import { createOpenLink, shareUrl } from '../services/invitations';
import { capture } from '../lib/analytics';
import { Screen, Card, Eyebrow, Title, Button, Spinner, ErrorState } from '../components/ui';

function firstName(fullName) {
  return (fullName || '').trim().split(/\s+/)[0] || 'Someone';
}

export default function Home() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [people, setPeople] = useState([]);
  const [pending, setPending] = useState([]);
  const [minting, setMinting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);        // invite/mint action errors
  const [loadError, setLoadError] = useState(false); // the people/links load failed

  const refresh = async () => {
    setLoading(true); setLoadError(false);
    try {
      const [conns, links] = await Promise.all([listConnections(), listPendingOpenLinks()]);
      setPeople(conns);
      setPending(links);
    } catch (err) {
      // A failed load must NOT fall through to the empty/invite screen — that
      // reads as "you have no people yet" and hides the failure.
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const share = async (url) => {
    if (navigator.share) {
      try { await navigator.share({ title: 'when', text: 'See when we\'re both free:', url }); return; }
      catch { /* user cancelled — fall through to copy */ }
    }
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const invite = async () => {
    setMinting(true); setError(null);
    try {
      const link = await createOpenLink();
      capture('invite_sent');
      const url = shareUrl(link.path);
      await share(url);
      await refresh();
    } catch (err) {
      setError('Couldn\'t create an invite link. Try again.');
    } finally {
      setMinting(false);
    }
  };

  if (loading) return <Screen><Spinner /></Screen>;

  const hasPeople = people.length > 0;

  return (
    <Screen>
      <div className="flex items-center justify-between">
        <Eyebrow>your people</Eyebrow>
        <button onClick={signOut} className="font-mono text-xs text-ink/40 hover:text-ink">sign out</button>
      </div>

      {loadError ? (
        <ErrorState message="Couldn't load your people. Check your connection and try again." onRetry={refresh} />
      ) : !hasPeople ? (
        <Card className="mt-4">
          <Title className="text-3xl">Invite someone you actually arrange things with.</Title>
          <p className="mt-4 font-sans text-base text-ink/70">
            Send them a link. When they open it, you'll both see the free time you
            have in common — nothing else.
          </p>
          <div className="mt-8">
            <Button onClick={invite} disabled={minting}>
              {minting ? 'Creating link…' : 'Create invite link'}
            </Button>
            {copied && <p className="mt-3 font-mono text-xs text-accent">Link copied.</p>}
          </div>
        </Card>
      ) : (
        <>
          <ul className="mt-4 divide-y divide-line/30 border border-line">
            {people.map((c) => (
              <li key={c.id}>
                <button
                  onClick={() => navigate(`/with/${c.principal?.id}`)}
                  className="flex w-full items-center justify-between px-5 py-4 text-left hover:bg-ink hover:text-paper"
                >
                  <span className="font-display text-xl">{firstName(c.principal?.full_name)}</span>
                  <span className="font-mono text-xs opacity-50">see overlap →</span>
                </button>
              </li>
            ))}
          </ul>

          <div className="mt-6">
            <Button onClick={invite} disabled={minting} variant="secondary">
              {minting ? 'Creating link…' : 'Invite someone'}
            </Button>
            {copied && <p className="mt-3 font-mono text-xs text-accent">Link copied.</p>}
          </div>
        </>
      )}

      {pending.length > 0 && (
        <div className="mt-8">
          <Eyebrow>pending — not yet accepted</Eyebrow>
          <ul className="mt-3 space-y-2">
            {pending.map((p) => (
              <li key={p.id} className="flex items-center justify-between border border-line/40 px-4 py-3">
                <span className="font-mono text-xs text-ink/50">
                  sent {new Date(p.created_at).toLocaleDateString()}
                </span>
                <button
                  onClick={() => share(shareUrl(`/i/${p.token}`))}
                  className="font-mono text-xs text-accent hover:underline"
                >
                  share again
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && <p className="mt-6 font-mono text-xs text-red-700">{error}</p>}
    </Screen>
  );
}
